import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import RevealWrapper from './RevealWrapper';

type FormState = 'idle' | 'sending' | 'success' | 'error';

interface FieldErrors {
  name?: string;
  email?: string;
  message?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_ENDPOINT = '/api/contact';
const WEBSITE_SUBJECT = 'Website Request';
const SUBJECT_FROM_QUERY: Record<string, string> = {
  'bug-report': 'Bug Report',
  'feature-request': 'Feature Request',
  partnership: 'Partnership',
  'website-request': WEBSITE_SUBJECT,
  general: 'General',
};

const PRODUCTS = [
  { value: 'defter-kasa', icon: 'ri-book-2-line', labelKey: 'contact.product.defterkasa', sub: 'LIVE' },
  { value: 'kozmik-gunluk', icon: 'ri-moon-line', labelKey: 'contact.product.kozmik', sub: 'DEV' },
  { value: 'anilar-muzesi', icon: 'ri-archive-line', labelKey: 'contact.product.anilar', sub: '' },
  { value: 'general', icon: 'ri-global-line', labelKey: 'contact.product.general', sub: '' },
];

const STATUS_COLORS: Record<string, string> = {
  LIVE: 'text-emerald-400',
  DEV:  'text-amber-400',
  SOON: 'text-neutral-500',
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  LIVE: 'products.status.live',
  DEV: 'products.status.dev',
  SOON: 'products.status.soon',
};

function ProductSelector({ value, onChange, disabled = false }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = PRODUCTS.find((p) => p.value === value) ?? PRODUCTS[PRODUCTS.length - 1];

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full flex items-center gap-3 bg-[#161616] border rounded-lg px-4 py-3 transition-all duration-200 text-left focus:outline-none ${
          disabled
            ? 'border-white/6 text-neutral-600 cursor-not-allowed opacity-60'
            : 'border-white/10 hover:border-white/20 cursor-pointer focus:border-white/30'
        }`}
      >
        <i className={`${selected.icon} text-sm text-neutral-400 shrink-0`} />
        <span className="flex-1 text-white text-sm">{t(selected.labelKey)}</span>
        {selected.sub && (
          <span className={`font-mono text-[10px] tracking-widest ${STATUS_COLORS[selected.sub] ?? 'text-neutral-600'}`}>
            {t(STATUS_LABEL_KEYS[selected.sub])}
          </span>
        )}
        <i className={`ri-arrow-down-s-line text-neutral-500 text-sm transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && !disabled && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full mt-2 bg-[#161616] border border-white/10 rounded-lg z-50 overflow-hidden py-1"
            style={{ boxShadow: '0 16px 48px rgba(0,0,0,0.7)' }}>
            {PRODUCTS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => { onChange(p.value); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors duration-150 cursor-pointer text-left hover:bg-white/[0.05] ${p.value === value ? 'bg-white/[0.07]' : ''}`}
              >
                <i className={`${p.icon} text-sm shrink-0 ${p.value === value ? 'text-white' : 'text-neutral-500'}`} />
                <span className={`flex-1 text-sm ${p.value === value ? 'text-white' : 'text-neutral-400'}`}>{t(p.labelKey)}</span>
                {p.sub && (
                  <span className={`font-mono text-[10px] tracking-widest ${STATUS_COLORS[p.sub] ?? 'text-neutral-600'}`}>{t(STATUS_LABEL_KEYS[p.sub])}</span>
                )}
                {p.value === value && <i className="ri-check-line text-white text-xs shrink-0 ml-1" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SuccessState({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStep(1), 100),
      setTimeout(() => setStep(2), 600),
      setTimeout(() => setStep(3), 1100),
      setTimeout(() => setStep(4), 1600),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="min-h-[420px] flex flex-col items-center justify-center relative overflow-hidden">
      <style>{`
        @keyframes glowPulse { 0%,100%{opacity:.4;transform:scale(1)} 50%{opacity:.8;transform:scale(1.08)} }
        @keyframes ringExpand { from{transform:scale(.6);opacity:0} to{transform:scale(1);opacity:1} }
        @keyframes particleFly { 0%{opacity:1;transform:translate(0,0) scale(1)} 100%{opacity:0;transform:translate(var(--tx),var(--ty)) scale(0)} }
      `}</style>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(16,185,129,0.08) 0%, transparent 70%)', animation: step >= 1 ? 'glowPulse 2.5s ease-in-out infinite' : 'none', opacity: step >= 1 ? 1 : 0, transition: 'opacity 0.6s ease' }} />
      {step >= 2 && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(12)].map((_, i) => {
            const angle = (i / 12) * 360;
            const dist = 60 + Math.random() * 40;
            return (
              <div key={i} className="absolute top-1/2 left-1/2 w-1 h-1 rounded-full bg-emerald-400"
                style={{ '--tx': `${Math.cos((angle * Math.PI) / 180) * dist}px`, '--ty': `${Math.sin((angle * Math.PI) / 180) * dist}px`, marginLeft: '-2px', marginTop: '-2px', animation: 'particleFly 0.8s ease-out both', animationDelay: `${i * 0.04}s` } as React.CSSProperties} />
            );
          })}
        </div>
      )}
      <div className="relative mb-8" style={{ animation: step >= 1 ? 'ringExpand 0.5s cubic-bezier(0.34,1.56,0.64,1) both' : 'none', opacity: step >= 1 ? 1 : 0 }}>
        <div className="absolute inset-[-12px] rounded-full border border-emerald-500/20 animate-ping" style={{ animationDuration: '2s' }} />
        <div className="absolute inset-[-6px] rounded-full border border-emerald-500/30" />
        <div className="w-20 h-20 rounded-full bg-[#0c0c0c] border-2 border-emerald-500 flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <polyline points="6,16 13,23 26,9" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="40"
              style={{ strokeDashoffset: step >= 2 ? 0 : 40, transition: 'stroke-dashoffset 0.5s cubic-bezier(0.16,1,0.3,1)' }} />
          </svg>
        </div>
      </div>
      <div className="w-full max-w-[200px] h-px bg-emerald-500/20 mb-8 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-transparent via-emerald-500 to-transparent" style={{ width: step >= 2 ? '100%' : '0%', transition: 'width 0.6s cubic-bezier(0.16,1,0.3,1)' }} />
      </div>
      <div className="text-center mb-2" style={{ opacity: step >= 3 ? 1 : 0, transform: step >= 3 ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.5s cubic-bezier(0.16,1,0.3,1)' }}>
        <p className="font-mono text-[10px] text-emerald-500/60 tracking-[0.25em] uppercase mb-3">{t('contact.success.thanks')}</p>
        <p className="font-black text-white text-3xl tracking-tight leading-none mb-2">{t('contact.success.title')}</p>
        <p className="text-neutral-500 text-sm">{t('contact.success.body')}</p>
      </div>
      <div className="mt-10" style={{ opacity: step >= 4 ? 1 : 0, transform: step >= 4 ? 'translateY(0)' : 'translateY(8px)', transition: 'all 0.4s ease' }}>
        <button onClick={onReset} className="group flex items-center gap-2 font-mono text-xs text-neutral-600 hover:text-white tracking-widest uppercase transition-colors duration-200 cursor-pointer">
          <span className="w-4 h-px bg-neutral-700 group-hover:bg-white group-hover:w-6 transition-all duration-300" />
          {t('contact.success.another')}
        </button>
      </div>
    </div>
  );
}

export default function ContactSection() {
  const { t } = useTranslation();
  const location = useLocation();
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [errorText, setErrorText] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('general');
  const [selectedSubject, setSelectedSubject] = useState('General');
  const formRef = useRef<HTMLFormElement>(null);
  const isWebsiteSubject = selectedSubject === WEBSITE_SUBJECT;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productFromUrl = params.get('contactProduct');
    const subjectFromUrl = params.get('contactSubject');

    if (productFromUrl && PRODUCTS.some((p) => p.value === productFromUrl)) {
      setSelectedProduct(productFromUrl);
    }

    if (subjectFromUrl) {
      const normalized = subjectFromUrl.trim().toLowerCase();
      const mappedSubject = SUBJECT_FROM_QUERY[normalized];
      if (mappedSubject) {
        setSelectedSubject(mappedSubject);
      }
    }

    if (location.hash === '#contact') {
      const el = document.getElementById('contact');
      if (el) {
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, [location.search, location.hash]);

  useEffect(() => {
    if (isWebsiteSubject && selectedProduct !== 'general') {
      setSelectedProduct('general');
    }
  }, [isWebsiteSubject, selectedProduct]);

  const validate = (form: HTMLFormElement): FieldErrors => {
    const errs: FieldErrors = {};
    const name = (form.elements.namedItem('name') as HTMLInputElement)?.value.trim();
    const email = (form.elements.namedItem('email') as HTMLInputElement)?.value.trim();
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value.trim();
    if (!name) errs.name = t('contact.err.nameRequired');
    if (!email) { errs.email = t('contact.err.emailRequired'); }
    else if (!EMAIL_REGEX.test(email)) { errs.email = t('contact.err.emailInvalid'); }
    if (!message) errs.message = t('contact.err.messageRequired');
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;
    const errs = validate(form);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setErrorText('');
    setFormState('sending');
    const formData = new FormData(form);

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const subject = selectedSubject.trim() || 'General';
    const body = String(formData.get('message') ?? '').trim();
    const selectedProductLabel = t(
      PRODUCTS.find((p) => p.value === selectedProduct)?.labelKey ?? 'contact.product.general'
    );

    const payload = {
      name,
      email,
      subject,
      product: selectedProductLabel,
      message: body,
      source: 'homepage-contact',
    };

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let result: { success?: boolean | string; message?: string } | null = null;
      const responseType = res.headers.get('content-type') ?? '';
      if (responseType.includes('application/json')) {
        result = await res.json().catch(() => null) as { success?: boolean | string; message?: string } | null;
      } else {
        const text = await res.text().catch(() => '');
        if (text) {
          try {
            result = JSON.parse(text) as { success?: boolean | string; message?: string };
          } catch {
            result = { message: text };
          }
        }
      }

      if (!res.ok) {
        console.error('[ContactSection] Contact API request failed', { status: res.status, result });
        setErrorText(t('contact.form.error'));
        setFormState('error');
        return;
      }

      const success = result?.success === true || result?.success === 'true' || result?.success === undefined;

      if (success) {
        setFormState('success');
        setShowSuccess(true);
        form.reset();
        setCharCount(0);
        setSelectedProduct('general');
        setSelectedSubject('General');
      } else {
        console.error('[ContactSection] Contact API returned unsuccessful response', result);
        setErrorText(t('contact.form.error'));
        setFormState('error');
      }
    } catch {
      setErrorText(t('contact.form.error'));
      setFormState('error');
    }
  };

  const handleFieldChange = (field: keyof FieldErrors) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    if (val && !EMAIL_REGEX.test(val)) setErrors((prev) => ({ ...prev, email: t('contact.err.emailInvalid') }));
  };

  const steps = [t('contact.step1'), t('contact.step2'), t('contact.step3')];

  const inputCls = (err?: string) =>
    `w-full bg-[#161616] border rounded-lg text-white text-sm px-4 py-3 placeholder-neutral-600 focus:outline-none transition-all duration-200 ${
      err ? 'border-red-500/50 focus:border-red-500/70' : 'border-white/10 focus:border-white/30 hover:border-white/20'
    }`;

  return (
    <section id="contact" className="bg-[#0c0c0c] border-t border-white/5">
      <div className="px-6 lg:px-14 py-10 border-b border-white/5">
        <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('contact.label')}</span>
      </div>

      <div className="px-6 lg:px-14 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-20 items-start">

          {/* Left */}
          <RevealWrapper direction="left">
            <div>
              <h2 className="font-black text-white text-5xl lg:text-6xl tracking-tight leading-none mb-10">
                {t('contact.title')}<br />
                <span className="text-neutral-600">{t('contact.title.sub')}</span>
              </h2>
              <p className="text-neutral-500 text-sm leading-loose max-w-sm mb-12">
                {t('contact.description')}
              </p>
              <div className="space-y-0 border border-white/5 rounded-xl overflow-hidden">
                {steps.map((step, i) => (
                  <div key={i} className={`flex items-center gap-5 px-6 py-5 ${i < steps.length - 1 ? 'border-b border-white/5' : ''} hover:bg-white/[0.02] transition-colors duration-200`}>
                    <span className="font-mono text-xs text-neutral-700 w-6 shrink-0">0{i + 1}</span>
                    <span className="text-neutral-400 text-sm">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </RevealWrapper>

          {/* Right — Stripe-style card */}
          <RevealWrapper direction="right" delay={150}>
            <div
              className="rounded-2xl overflow-hidden transition-all duration-500"
              style={{
                background: 'linear-gradient(145deg, #161616 0%, #111 100%)',
                border: showSuccess ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(255,255,255,0.08)',
                boxShadow: showSuccess
                  ? '0 0 0 1px rgba(16,185,129,0.1), 0 32px 64px -16px rgba(0,0,0,0.8)'
                  : '0 32px 64px -16px rgba(0,0,0,0.6)',
              }}
            >
              {/* Card header stripe */}
              <div className="px-8 py-5 border-b border-white/[0.06] flex items-center justify-between"
                style={{ background: 'rgba(255,255,255,0.02)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-white/20" />
                  <p className="font-mono text-xs text-neutral-500 tracking-widest uppercase">{t('contact.form.title')}</p>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full border transition-all duration-500 ${showSuccess ? 'border-emerald-500/30 bg-emerald-500/8' : 'border-white/8 bg-white/[0.03]'}`}>
                  <i className={`text-xs transition-all duration-500 ${showSuccess ? 'ri-check-line text-emerald-400' : 'ri-send-plane-line text-neutral-600'}`} />
                  <span className={`font-mono text-[10px] tracking-widest transition-all duration-500 ${showSuccess ? 'text-emerald-400' : 'text-neutral-600'}`}>
                    {showSuccess ? t('contact.form.state.sent') : t('contact.form.state.ready')}
                  </span>
                </div>
              </div>

              <div className="p-8">
                {showSuccess ? (
                  <SuccessState onReset={() => { setFormState('idle'); setShowSuccess(false); setErrorText(''); }} />
                ) : (
                  <form id="contact-form" data-readdy-form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-5">

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase block mb-2">
                          {t('contact.form.name')} <span className="text-neutral-700">*</span>
                        </label>
                        <input type="text" name="name" placeholder={t('contact.form.namePlaceholder')}
                          onChange={() => handleFieldChange('name')}
                          className={inputCls(errors.name)} />
                        {errors.name && (
                          <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] text-red-400">
                            <i className="ri-error-warning-fill text-xs" />{errors.name}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase block mb-2">
                          {t('contact.form.email')} <span className="text-neutral-700">*</span>
                        </label>
                        <input type="email" name="email" placeholder={t('contact.form.emailPlaceholder')}
                          onChange={() => handleFieldChange('email')} onBlur={handleEmailBlur}
                          className={inputCls(errors.email)} />
                        {errors.email && (
                          <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] text-red-400">
                            <i className="ri-error-warning-fill text-xs" />{errors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase block mb-2">{t('contact.form.subject')}</label>
                        <select
                          name="subject"
                          value={selectedSubject}
                          onChange={(e) => setSelectedSubject(e.target.value)}
                          className={inputCls()}
                        >
                          <option value="Bug Report">{t('contact.form.subject.bug')}</option>
                          <option value="Feature Request">{t('contact.form.subject.feature')}</option>
                          <option value="Partnership">{t('contact.form.subject.partnership')}</option>
                          <option value={WEBSITE_SUBJECT}>{t('contact.form.subject.website')}</option>
                          <option value="General">{t('contact.form.subject.general')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase block mb-2">
                          {t('contact.form.product')}
                        </label>
                        <ProductSelector value={selectedProduct} onChange={setSelectedProduct} disabled={isWebsiteSubject} />
                        <input type="hidden" name="product" value={selectedProduct} />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="font-mono text-[10px] text-neutral-500 tracking-widest uppercase">
                          {t('contact.form.message')} <span className="text-neutral-700">*</span>
                        </label>
                        <span className={`font-mono text-[10px] ${charCount > 450 ? 'text-neutral-400' : 'text-neutral-700'}`}>{charCount}/500</span>
                      </div>
                      <textarea name="message" rows={5} maxLength={500}
                        placeholder={t('contact.form.messagePlaceholder')}
                        onChange={(e) => { setCharCount(e.target.value.length); handleFieldChange('message'); }}
                        className={inputCls(errors.message)} />
                      {errors.message && (
                        <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] text-red-400">
                          <i className="ri-error-warning-fill text-xs" />{errors.message}
                        </p>
                      )}
                    </div>

                    {formState === 'error' && (
                      <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-red-500/20 bg-red-500/5">
                        <i className="ri-error-warning-line text-red-400 text-sm" />
                        <span className="font-mono text-xs text-red-400">{errorText || t('contact.form.error')}</span>
                      </div>
                    )}

                    {/* Divider */}
                    <div className="border-t border-white/[0.06] pt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <p className="font-mono text-[10px] text-neutral-700 tracking-wider">{t('contact.form.footer')}</p>
                      <button
                        type="submit"
                        disabled={formState === 'sending' || charCount > 500}
                        className="group relative w-full sm:w-auto flex items-center justify-center gap-2.5 px-6 py-3 rounded-lg font-mono text-xs font-bold tracking-widest uppercase overflow-hidden transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer bg-white text-black hover:bg-neutral-100 active:scale-[0.98]"
                      >
                        {formState === 'sending' ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-sm" />
                            <span className="sm:hidden">Gönderiliyor...</span>
                            <span className="hidden sm:inline">{t('contact.form.sending')}</span>
                          </>
                        ) : (
                          <>
                            <i className="hidden sm:inline ri-send-plane-fill text-sm" />
                            <span className="sm:hidden">Gönder</span>
                            <span className="hidden sm:inline">{t('contact.form.send')}</span>
                          </>
                        )}
                      </button>
                    </div>

                  </form>
                )}
              </div>
            </div>
          </RevealWrapper>
        </div>
      </div>
    </section>
  );
}

